import { useEffect, useState } from 'react';

import { Icon } from '../components/Icon';
import { API_URL, WHATSAPP } from '../config/company';

// Paystack sends people here after they add a card (PAYSTACK_CALLBACK_URL in
// the backend, ../Hethera/docs/ops-and-account-acceptance-criteria.md C).
// The page asks the API whether the card was saved, shows the result, and
// on success returns the user to the WhatsApp chat on its own.
//
// Prerendered like every page, so the first render must match on the server:
// it shows "checking" and reads the URL only inside the effect. The Paystack
// reference is removed from the address bar at once and never stored.

type Phase = 'checking' | 'saved' | 'pending' | 'failed' | 'no-reference';

// Five checks in 18 s: the API allows 5 a minute per visitor
// (CARD_STATUS_PER_MINUTE), and Paystack's webhook usually lands within seconds.
const CHECK_AT_MS = [0, 2000, 5000, 10000, 18000];
const REDIRECT_SECONDS = { saved: 5, pending: 8 } as const;

async function cardStatus(reference: string): Promise<'saved' | 'pending' | 'failed'> {
  try {
    const response = await fetch(
      `${API_URL}/api/v1/paystack/card-status?reference=${encodeURIComponent(reference)}`,
      { headers: { Accept: 'application/json' }, cache: 'no-store', referrerPolicy: 'no-referrer' },
    );
    if (!response.ok) return 'pending';
    const body = await response.json();
    const status = body?.data?.status;
    return status === 'saved' || status === 'failed' ? status : 'pending';
  } catch {
    return 'pending'; // offline, blocked or the API is down: we can't tell yet
  }
}

export function PaymentSuccessPage() {
  const [phase, setPhase] = useState<Phase>('checking');
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reference = params.get('reference') || params.get('trxref');
    window.history.replaceState(null, '', window.location.pathname);
    if (!reference) {
      setPhase('no-reference');
      return;
    }

    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const started = Date.now();
    const poll = async (attempt: number) => {
      const status = await cardStatus(reference);
      if (stopped) return;
      if (status !== 'pending') {
        setPhase(status);
      } else if (attempt + 1 >= CHECK_AT_MS.length) {
        setPhase('pending');
      } else {
        const wait = Math.max(0, started + CHECK_AT_MS[attempt + 1] - Date.now());
        timer = setTimeout(() => poll(attempt + 1), wait);
      }
    };
    poll(0);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, []);

  // Saved, or still confirming after the wait: count down, then open WhatsApp.
  useEffect(() => {
    if (phase !== 'saved' && phase !== 'pending') return;
    let left: number = REDIRECT_SECONDS[phase];
    setSecondsLeft(left);
    const timer = setInterval(() => {
      left -= 1;
      setSecondsLeft(left);
      if (left <= 0) {
        clearInterval(timer);
        window.location.assign(WHATSAPP.href);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [phase]);

  return (
    <section className="paid" aria-live="polite">
      <div className="paid__card">
        {phase === 'checking' && (
          <>
            <span className="paid__spinner" role="img" aria-label="Checking" />
            <h1>Confirming your payment…</h1>
            <p>This takes a few seconds. Please keep this page open.</p>
          </>
        )}

        {phase === 'saved' && (
          <>
            <span className="paid__icon paid__icon--ok">
              <Icon name="check" size={36} color="var(--ink)" strokeWidth={2.6} />
            </span>
            <h1>Payment successful</h1>
            <p>
              Your card is saved. The small verification charge will be refunded. Go back to WhatsApp to
              finish what you were doing.
            </p>
            <Redirecting secondsLeft={secondsLeft} total={REDIRECT_SECONDS.saved} />
          </>
        )}

        {phase === 'pending' && (
          <>
            <span className="paid__icon">
              <Icon name="card" size={32} color="var(--ink)" />
            </span>
            <h1>Almost there</h1>
            <p>We're confirming your card with Paystack. You'll get a message on WhatsApp in a moment.</p>
            <Redirecting secondsLeft={secondsLeft} total={REDIRECT_SECONDS.pending} />
          </>
        )}

        {phase === 'failed' && (
          <>
            <span className="paid__icon">
              <Icon name="card" size={32} color="var(--ink)" />
            </span>
            <h1>Your card wasn't added</h1>
            <p>Nothing else was charged. Go back to WhatsApp and ask Hethera for a new link to try again.</p>
            <BackButton />
          </>
        )}

        {phase === 'no-reference' && (
          <>
            <span className="paid__icon">
              <Icon name="chat" size={32} color="var(--ink)" />
            </span>
            <h1>Back to WhatsApp</h1>
            <p>If you've just added a card, Hethera will confirm it in the chat.</p>
            <BackButton />
          </>
        )}
      </div>
    </section>
  );
}

function Redirecting({ secondsLeft, total }: { secondsLeft: number | null; total: number }) {
  const left = secondsLeft ?? total;
  return (
    <>
      <div className="paid__bar" aria-hidden="true">
        <span style={{ width: `${((total - left) / total) * 100}%` }} />
      </div>
      <span className="paid__count">
        Taking you back to WhatsApp in {Math.max(left, 0)} second{left === 1 ? '' : 's'}…
      </span>
      <BackButton label="Open WhatsApp now" />
    </>
  );
}

function BackButton({ label = 'Back to WhatsApp' }: { label?: string }) {
  return (
    <a className="btn btn-dark btn-lg" href={WHATSAPP.href}>
      <Icon name="chat" />
      {label}
    </a>
  );
}
